import { Link } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { Badge } from "@/components/ui/badge";
import { StageBadge } from '@/components/shared/StageBadge';
import { PaymentSummary } from '@/components/shared/PaymentSummary';
import { DocumentSection } from '@/components/shared/DocumentSection';
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import {
  CheckCircle2,
  Users,
  ExternalLink,
  Plus,
  Pencil,
  Trash2,
  Download,
  FileCheck,
  AlertTriangle,
} from "lucide-react";
import { router } from "@inertiajs/react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useState, useEffect } from "react";
import { FormationPrincipal, FormationProposedName, FormationSupports } from "../../../types";
import { FormationProposedNameSheet } from "../../../components/formations/FormationProposedNameSheet";
import { FormationBusinessAddressSheet } from "../../../components/formations/FormationBusinessAddressSheet";
import { FormationContactInfoSheet } from "../../../components/formations/FormationContactInfoSheet";
import { AddPrincipalDialog } from "../../Formations/dialogs/AddPrincipalDialog";
import { EditPrincipalDialog } from "../../Formations/dialogs/EditPrincipalDialog";
import {
  FormationBusinessAddress,
  FormationContactInfo as FormationContactInfoType,
} from "../../../types";
import { NameClearanceDialog } from "./dialogs/NameClearanceDialog";
import { StateFilingDialog } from "./dialogs/StateFilingDialog";
import { CertificateDialog } from "./dialogs/CertificateDialog";
import { RegisteredAgentAddressDialog, RegisteredAgent } from "./dialogs/RegisteredAgentAddressDialog";
import { RegisteredAgentSection } from "./components/RegisteredAgentSection";
import { TaxRegistrationSection, TaxRegistrationProgress } from "./components/TaxRegistrationSection";
import { ActivitySection, FormationEvent } from "./components/ActivitySection";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

function SortableProposedNameItem({
  pn,
  onEdit,
  onDelete,
}: {
  pn: any;
  onEdit: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: pn.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing p-1 -ml-1"
          aria-label={`Reorder ${pn.name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" aria-hidden="true" />
        </button>
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
          <span className="text-xs font-medium text-muted-foreground">
            {pn.position + 1}
          </span>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground truncate">
            {pn.name}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          aria-label={`Edit ${pn.name}`}
          onClick={onEdit}
        >
          <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          aria-label={`Remove ${pn.name}`}
          onClick={onDelete}
        >
          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

type DocumentAttachment = {
  id: string;
  name: string;
  file_url?: string | null;
  blob_signed_id?: string | null;
  filename?: string | null;
  content_type?: string | null;
  created_at: string;
};

type Formation = {
  id: string;
  legal_name: string;
  assumed_name: string | null;
  stage: string;
  entity_name: string;
  jurisdiction_code: string;
  account_name: string;
  account_slug: string;
  payment: {
    id: string;
    service_fee_cents: number;
    state_fee_cents: number;
    total_cents: number;
    paid: boolean;
    paid_at: string | null;
    invoice_number?: string | null;
    receipt_number?: string | null;
  } | null;
  state_filing: {
    id: string;
    jurisdiction_reference: string | null;
    submitted_at: string | null;
    accepted: boolean;
    rejected: boolean;
    pending: boolean;
    acceptance: {
      certificate_number: string | null;
      accepted_at: string | null;
      certificate_attached: boolean;
      certificate_url: string | null;
    } | null;
    rejection: { reason: string; notes: string | null } | null;
  } | null;
  name_clearance: { cleared_name: string; created_at: string } | null;
  completion: { id: string; notes: string | null; created_at: string } | null;
  business_address_provider: string | null;
  registered_agents: RegisteredAgent[];
  tax_registration_progress: TaxRegistrationProgress[];
  principals: FormationPrincipal[];
  application_documents: DocumentAttachment[];
  filing_documents: DocumentAttachment[];
  completion_documents: DocumentAttachment[];
  proposed_names: { id: string; name: string; position: number }[];
  business_addresses: FormationBusinessAddress[];
  contact_infos: FormationContactInfoType[];
  created_at: string;
};

type Entity = {
  id: string;
  name: string;
  code: string;
  jurisdiction_code: string;
  state_fee_cents: number;
  service_fee_cents: number;
};

type Props = {
  formation: Formation;
  entity: Entity;
  supports: FormationSupports;
  countries: { id: string; code: string; name: string }[];
  states: { id: string; code: string; name: string; country_id: string }[];
  local_areas: { id: string; code: string; name: string; state_id: string }[];
  events: FormationEvent[];
};

export default function BackofficeFormationsShow({
  formation,
  entity,
  supports,
  countries,
  states,
  local_areas,
  events,
}: Props) {
  const confirm = useConfirm();
  const [nameClearanceSheetOpen, setNameClearanceSheetOpen] = useState(false);
  const [stateFilingSheetOpen, setStateFilingSheetOpen] = useState(false);
  const [certificateSheetOpen, setCertificateSheetOpen] = useState(false);
  const [registeredAgentSheetOpen, setRegisteredAgentSheetOpen] = useState(false);
  const [editRegisteredAgent, setEditRegisteredAgent] = useState<RegisteredAgent | null>(null);
  const [proposedNameSheetOpen, setProposedNameSheetOpen] = useState(false);
  const [editProposedName, setEditProposedName] = useState<
    (typeof formation.proposed_names)[number] | null
  >(null);
  const [proposedNames, setProposedNames] = useState<
    typeof formation.proposed_names
  >(formation?.proposed_names || []);

  const [businessAddressSheetOpen, setBusinessAddressSheetOpen] =
    useState(false);
  const [editBusinessAddress, setEditBusinessAddress] =
    useState<FormationBusinessAddress | null>(null);

  const [contactInfoSheetOpen, setContactInfoSheetOpen] = useState(false);
  const [editContactInfo, setEditContactInfo] =
    useState<FormationContactInfoType | null>(null);

  const [addPrincipalSheetOpen, setAddPrincipalSheetOpen] = useState(false);
  const [editPrincipalSheetOpen, setEditPrincipalSheetOpen] = useState(false);
  const [editPrincipal, setEditPrincipal] = useState<FormationPrincipal | null>(null);

  useEffect(() => {
    if (formation?.proposed_names) setProposedNames(formation.proposed_names);
  }, [formation?.proposed_names]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setProposedNames((items: FormationProposedName[]) => {
        const oldIndex = items.findIndex(
          (i: FormationProposedName) => i.id === active.id,
        );
        const newIndex = items.findIndex(
          (i: FormationProposedName) => i.id === over.id,
        );
        const newArray = arrayMove(items, oldIndex, newIndex);

        // Optimistically update positions for UI display
        const updatedArray = newArray.map(
          (item: FormationProposedName, index: number) => ({
            ...item,
            position: index,
          }),
        );

        router.patch(
          `/backoffice/formations/${formation.id}/proposed_names/reorder`,
          {
            ordered_ids: updatedArray.map((i: FormationProposedName) => i.id),
          },
          { preserveScroll: true },
        );

        return updatedArray;
      });
    }
  }

  return (
    <BackofficeLayout
      title={formation.legal_name}
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Formations", href: "/backoffice/formations" },
        { label: formation.legal_name },
      ]}
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {formation.legal_name}
          </h1>
          <StageBadge stage={formation.stage} variant="formation" />
        </div>
        <p className="text-sm text-muted-foreground">
          {entity.name} · {entity.jurisdiction_code}
          {formation.assumed_name && ` · DBA: ${formation.assumed_name}`}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Account:{" "}
          <Link
            href={`/${formation.account_slug}/dashboard`}
            className="hover:underline inline-flex items-center gap-1"
          >
            {formation.account_name} <ExternalLink className="w-3 h-3" />
          </Link>
          {" · "}Created {formatDate(formation.created_at)}
        </p>
      </div>

      <div className="space-y-6">
        {/* Payment */}
        <PaymentSummary
          payment={formation.payment ? {
            id: formation.payment.id,
            service_fee_cents: formation.payment.service_fee_cents,
            gov_fee_cents: formation.payment.state_fee_cents,
            total_cents: formation.payment.total_cents,
            paid: formation.payment.paid,
            paid_at: formation.payment.paid_at,
            invoice_number: formation.payment.invoice_number,
            receipt_number: formation.payment.receipt_number,
          } : null}
          adminActions={formation.payment ? {
            invoicePath: `/backoffice/formations/${formation.id}/payments/${formation.payment.id}/invoice`,
          } : undefined}
          collapsible={false}
        />

        {/* Name Clearance */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Name Clearance</h2>
            {!formation.name_clearance && formation.payment?.paid && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setNameClearanceSheetOpen(true)}>
                Clear a name
              </Button>
            )}
          </div>
          <div className="px-4 py-3 text-sm">
            {formation.name_clearance ? (
              <div className="flex items-center gap-1.5 text-green-600 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                &quot;{formation.name_clearance.cleared_name}&quot; cleared {formatDate(formation.name_clearance.created_at)}
              </div>
            ) : (
              <span className="text-muted-foreground">
                {formation.payment?.paid ? "Not cleared yet." : "Payment is required before a name can be cleared."}
              </span>
            )}
          </div>
        </section>

        {/* State Filing */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              State Filing
            </h2>
            {!formation.state_filing && formation.payment?.paid && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setStateFilingSheetOpen(true)}>
                Record filing
              </Button>
            )}
            {formation.state_filing?.accepted && (
              <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                <CheckCircle2 className="w-3 h-3" />
                Certificate issued
              </span>
            )}
            {formation.state_filing?.rejected && (
              <Badge className="bg-red-100 text-red-700 font-normal text-[11px]">
                Rejected
              </Badge>
            )}
            {formation.state_filing?.pending && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCertificateSheetOpen(true)}>
                Record certificate
              </Button>
            )}
          </div>
          {formation.state_filing ? (
            <div className="px-4 py-3 space-y-1.5 text-sm">
              {formation.state_filing.jurisdiction_reference && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-mono text-xs">
                    {formation.state_filing.jurisdiction_reference}
                  </span>
                </div>
              )}
              {formation.state_filing.submitted_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted</span>
                  <span>{formatDate(formation.state_filing.submitted_at)}</span>
                </div>
              )}
              {formation.state_filing.rejection && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 mt-2">
                  <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <span className="text-xs text-destructive">{formation.state_filing.rejection.reason}</span>
                </div>
              )}
              {formation.state_filing.acceptance && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Certificate number</span>
                    <span className="font-mono text-xs">{formation.state_filing.acceptance.certificate_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Accepted</span>
                    <span>{formation.state_filing.acceptance.accepted_at && formatDate(formation.state_filing.acceptance.accepted_at)}</span>
                  </div>
                  {formation.state_filing.acceptance.certificate_url && (
                    <a
                      href={formation.state_filing.acceptance.certificate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-foreground hover:underline mt-1"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download certificate
                    </a>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              {formation.payment?.paid ? "Not filed yet." : "Payment is required before filing."}
            </div>
          )}
        </section>

        {/* Proposed Names */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              Proposed Names
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setEditProposedName(null);
                setProposedNameSheetOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add name
            </Button>
          </div>
          <div className="px-4">
            {proposedNames.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={proposedNames.map(
                    (pn: FormationProposedName) => pn.id,
                  )}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="divide-y">
                    {proposedNames.map((pn: FormationProposedName) => (
                      <SortableProposedNameItem
                        key={pn.id}
                        pn={pn}
                        onEdit={() => {
                          setEditProposedName(pn);
                          setProposedNameSheetOpen(true);
                        }}
                        onDelete={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              description: "Remove this proposed name?",
                              confirmLabel: "Remove",
                              destructive: true,
                            })
                          ) {
                            router.delete(
                              `/backoffice/formations/${formation.id}/proposed_names/${pn.id}`,
                            );
                          }
                        }}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No proposed names added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditProposedName(null);
                    setProposedNameSheetOpen(true);
                  }}
                >
                  Add first choice
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Business Addresses */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              Business Address
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setEditBusinessAddress(null);
                setBusinessAddressSheetOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add address
            </Button>
          </div>
          <div className="px-4">
            {formation.business_addresses?.length > 0 ? (
              <div className="divide-y">
                {formation.business_addresses.map(
                  (addr: FormationBusinessAddress) => (
                    <div
                      key={addr.id}
                      className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                          {addr.label && (
                            <div className="text-xs text-muted-foreground mb-0.5">
                              {addr.label}
                            </div>
                          )}
                          <div className="text-sm font-medium text-foreground">
                            {addr.street_address_1}
                          </div>
                          {addr.street_address_2 && (
                            <div className="text-xs text-muted-foreground">
                              {addr.street_address_2}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {addr.city}
                            {addr.state_province &&
                              `, ${addr.state_province}`}{" "}
                            {addr.postal_code} · {addr.country_code}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          aria-label={`Edit ${addr.label || addr.street_address_1}`}
                          onClick={() => {
                            setEditBusinessAddress(addr);
                            setBusinessAddressSheetOpen(true);
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          aria-label={`Remove ${addr.label || addr.street_address_1}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (
                              await confirm({
                                description: "Remove this address?",
                                confirmLabel: "Remove",
                                destructive: true,
                              })
                            )
                              router.delete(
                                `/backoffice/formations/${formation.id}/business_addresses/${addr.id}`,
                              );
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No business address added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditBusinessAddress(null);
                    setBusinessAddressSheetOpen(true);
                  }}
                >
                  Add address
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Contact Info */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              Contact Information
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setEditContactInfo(null);
                setContactInfoSheetOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add contact
            </Button>
          </div>
          <div className="px-4">
            {formation.contact_infos?.length > 0 ? (
              <div className="divide-y">
                {formation.contact_infos.map((ci: FormationContactInfoType) => (
                  <div
                    key={ci.id}
                    className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors"
                  >
                    <div className="min-w-0">
                      {ci.label && (
                        <div className="text-xs text-muted-foreground mb-0.5">
                          {ci.label}
                        </div>
                      )}
                      <div className="text-sm font-medium text-foreground">
                        {ci.email}
                      </div>
                      {ci.phone && (
                        <div className="text-xs text-muted-foreground">
                          {ci.phone}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        aria-label={`Edit ${ci.label || ci.email}`}
                        onClick={() => {
                          setEditContactInfo(ci);
                          setContactInfoSheetOpen(true);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${ci.label || ci.email}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              description: "Remove this contact?",
                              confirmLabel: "Remove",
                              destructive: true,
                            })
                          )
                            router.delete(
                              `/backoffice/formations/${formation.id}/contact_infos/${ci.id}`,
                            );
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No contact information added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditContactInfo(null);
                    setContactInfoSheetOpen(true);
                  }}
                >
                  Add contact
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* People */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">People</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {formation.principals.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setAddPrincipalSheetOpen(true)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add person
              </Button>
            </div>
          </div>
          <div className="px-4">
            {formation.principals.length > 0 ? (
              <div className="divide-y">
                {formation.principals.map((p) => (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Edit ${p.full_name}`}
                    className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      setEditPrincipal(p);
                      setEditPrincipalSheetOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setEditPrincipal(p);
                        setEditPrincipalSheetOpen(true);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <span className="text-xs font-medium text-muted-foreground">
                          {p.full_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {p.full_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.email && <span>{p.email} · </span>}
                          {Object.keys(p.roles || {}).length > 0 ? (
                            Object.keys(p.roles)
                              .map(
                                (r) =>
                                  r.charAt(0).toUpperCase() +
                                  r.slice(1).replace("_", " "),
                              )
                              .join(", ")
                          ) : (
                            <span className="capitalize">
                              {p.principal_kind}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.kyc_verified && (
                        <span className="flex items-center gap-1 text-xs text-green-600 mr-2">
                          <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                          <span className="sr-only">KYC verified</span>
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                        aria-label={`Remove ${p.full_name}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              description: "Remove this person?",
                              confirmLabel: "Remove",
                              destructive: true,
                            })
                          ) {
                            router.delete(
                              `/backoffice/formations/${formation.id}/principals/${p.id}`,
                            );
                          }
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <Users className="w-5 h-5 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground mb-2">
                  No people added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddPrincipalSheetOpen(true)}
                >
                  Add person
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Registered Agent */}
        <RegisteredAgentSection
          registeredAgents={formation.registered_agents}
          onEdit={(agent) => {
            setEditRegisteredAgent(agent);
            setRegisteredAgentSheetOpen(true);
          }}
        />

        {/* Tax Registrations (EIN included — see TaxRegistrationType::EIN_CODE) */}
        <TaxRegistrationSection formationId={formation.id} taxRegistrationProgress={formation.tax_registration_progress} />

        {/* Completion — the tracker's final "Documents ready" milestone */}
        {formation.state_filing?.accepted && (
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-sm font-medium text-foreground">Completion</h2>
              </div>
              {formation.completion ? (
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  Complete since {formatDate(formation.completion.created_at)}
                </span>
              ) : (
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={async () => {
                    if (await confirm({ description: "Mark this formation complete? The founder will be emailed.", confirmLabel: "Mark complete" })) {
                      router.post(`/backoffice/formations/${formation.id}/completion`);
                    }
                  }}
                >
                  Mark complete
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Activity — audit trail of every staff action on this formation */}
        <ActivitySection events={events} />

        {/* Documents */}
        <DocumentSection
          categories={[
            {
              label: "Application Documents",
              documents: formation.application_documents || [],
              uploadPath: `/backoffice/formations/${formation.id}/application_documents`,
              deletePath: `/backoffice/formations/${formation.id}/application_documents`,
            },
            {
              label: "Filing Documents",
              documents: formation.filing_documents || [],
              uploadPath: `/backoffice/formations/${formation.id}/filing_documents`,
              deletePath: `/backoffice/formations/${formation.id}/filing_documents`,
            },
            {
              label: "Completion Documents",
              documents: formation.completion_documents || [],
              uploadPath: `/backoffice/formations/${formation.id}/completion_documents`,
              deletePath: `/backoffice/formations/${formation.id}/completion_documents`,
            },
          ]}
          templates={(formation as any).templates || []}
        />
      </div>

      <FormationProposedNameSheet
        open={proposedNameSheetOpen}
        onOpenChange={setProposedNameSheetOpen}
        formationId={formation.id}
        proposedName={editProposedName}
        basePath="/backoffice"
      />
      <FormationBusinessAddressSheet
        open={businessAddressSheetOpen}
        onOpenChange={setBusinessAddressSheetOpen}
        formationId={formation.id}
        address={editBusinessAddress}
        basePath="/backoffice"
        countries={countries}
        states={states}
      />
      <FormationContactInfoSheet
        open={contactInfoSheetOpen}
        onOpenChange={setContactInfoSheetOpen}
        formationId={formation.id}
        contact={editContactInfo}
        basePath="/backoffice"
      />

      {/* Principal Dialogs */}
      <AddPrincipalDialog
        formationId={formation.id}
        formationName={formation.legal_name}
        supports={supports}
        countries={countries}
        states={states}
        localAreas={local_areas || []}
        open={addPrincipalSheetOpen}
        onOpenChange={setAddPrincipalSheetOpen}
        basePath="/backoffice"
      />
      <EditPrincipalDialog
        formationId={formation.id}
        formationName={formation.legal_name}
        supports={supports}
        principal={editPrincipal}
        countries={countries}
        states={states}
        localAreas={local_areas || []}
        open={editPrincipalSheetOpen}
        onOpenChange={setEditPrincipalSheetOpen}
        basePath="/backoffice"
      />

      {/* Fulfilment Dialogs (Phase 5) */}
      <NameClearanceDialog
        open={nameClearanceSheetOpen}
        onOpenChange={setNameClearanceSheetOpen}
        formationId={formation.id}
        legalName={formation.legal_name}
        proposedNames={formation.proposed_names}
      />
      <StateFilingDialog open={stateFilingSheetOpen} onOpenChange={setStateFilingSheetOpen} formationId={formation.id} />
      <CertificateDialog open={certificateSheetOpen} onOpenChange={setCertificateSheetOpen} formationId={formation.id} />
      <RegisteredAgentAddressDialog
        open={registeredAgentSheetOpen}
        onOpenChange={setRegisteredAgentSheetOpen}
        formationId={formation.id}
        registeredAgent={editRegisteredAgent}
      />
    </BackofficeLayout>
  );
}
